import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { useNamespace } from "../../hooks/useNamespace";
import { useAuth } from "../../contexts/AuthContext";
import { listInvoices, deleteInvoice } from "../../api/invoiceService";
import { listFirmProfiles } from "../../api/firmProfileService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, formatINRExact, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

// Admin-only list of generated tax invoices. New invoices come in via the
// "Generate Invoice" button on Payments Oversight — there is no direct
// "blank invoice" creation here.
export default function InvoicesPage() {
  const ns = useNamespace();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("payments.edit");
  const [filters, setFilters] = useState({
    q: "",
    firm_profile_id: "",
    gst_treatment: "", // "" = any, "gst", "no_gst"
    from: "",
    to: "",
  });
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [deletingId, setDeletingId] = useState(null);
  const [firmProfiles, setFirmProfiles] = useState([]);
  const firmOptions = useMemo(
    () => firmProfiles.map((f) => ({ id: f.id, label: f.firm_name })),
    [firmProfiles],
  );

  // Firm dropdown options — tiny list, fetched once.
  useEffect(() => {
    listFirmProfiles()
      .then((res) => setFirmProfiles(res?.data || []))
      .catch(() => setFirmProfiles([]));
  }, []);

  // Debounce only the free-text search; the other filters fire on change.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 250);
    return () => clearTimeout(t);
  }, [filters.q]);

  // Any filter change resets to page 1 so admins don't land on an empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, filters.firm_profile_id, filters.gst_treatment, filters.from, filters.to, perPage]);

  const queryParams = useMemo(() => {
    const p = { page, per_page: perPage };
    if (debouncedQ) p.q = debouncedQ;
    if (filters.firm_profile_id) p.firm_profile_id = filters.firm_profile_id;
    if (filters.gst_treatment) p.gst_treatment = filters.gst_treatment;
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    return p;
  }, [page, perPage, debouncedQ, filters.firm_profile_id, filters.gst_treatment, filters.from, filters.to]);

  const invoices = useApi(() => listInvoices(queryParams), [JSON.stringify(queryParams)]);
  const rows = paginatedRows(invoices.data);
  const meta = paginatedMeta(invoices.data);

  const hasActiveFilter = !!(filters.q || filters.firm_profile_id || filters.gst_treatment || filters.from || filters.to);
  const clearFilters = () =>
    setFilters({ q: "", firm_profile_id: "", gst_treatment: "", from: "", to: "" });

  const remove = async (inv) => {
    if (!window.confirm(
      `Delete invoice #${inv.invoice_no} for ${inv.buyer_name}? Invoice numbers are an integer sequence — you can re-issue with the same number afterwards if needed.`,
    )) return;

    setDeletingId(inv.id);
    try {
      await deleteInvoice(inv.id);
      showToast("success", "Invoice deleted");
      invoices.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Invoices</h1>
          <div className="page-sub">
            {meta?.total ?? rows.length} generated invoice{(meta?.total ?? rows.length) === 1 ? "" : "s"}
          </div>
        </div>
        <div className="page-actions">
          {canEdit && (
            <Link to={`${ns}/payments`} className="btn btn-secondary">
              <Icon name="plus" size={14} /> Generate from a bill
            </Link>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="row gap-md" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 2, minWidth: 220, marginBottom: 0 }}>
              <label className="field-label">Search</label>
              <div style={{ position: "relative" }}>
                <Icon
                  name="search"
                  size={13}
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
                />
                <input
                  className="input"
                  style={{ paddingLeft: 30 }}
                  placeholder="Invoice no, firm, buyer, or client ID…"
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                />
              </div>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
              <label className="field-label">Firm</label>
              <SearchableSelect
                value={filters.firm_profile_id}
                onChange={(v) => setFilters({ ...filters, firm_profile_id: v ?? "" })}
                options={firmOptions}
                placeholder="All firms"
                searchPlaceholder="Search firms…"
                width="100%"
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 130, marginBottom: 0 }}>
              <label className="field-label">GST</label>
              <select
                className="select"
                value={filters.gst_treatment}
                onChange={(e) => setFilters({ ...filters, gst_treatment: e.target.value })}
              >
                <option value="">Any</option>
                <option value="gst">GST</option>
                <option value="no_gst">No GST</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
              <label className="field-label">From</label>
              <input
                type="date"
                className="input"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
              <label className="field-label">To</label>
              <input
                type="date"
                className="input"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              />
            </div>
            {hasActiveFilter && (
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
                <Icon name="x" size={13} /> Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {invoices.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="file-text"
              title={hasActiveFilter ? "No invoices match these filters" : "No invoices yet"}
              hint={hasActiveFilter ? "Try clearing or widening a filter." : "Generate one from the Payments Oversight page."}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Invoice No.</th>
                  <th>Date</th>
                  <th>Firm</th>
                  <th>Buyer</th>
                  <th>GST</th>
                  <th>Total</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv, i) => (
                  <tr key={inv.id}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td className="t-mono">{inv.invoice_no}</td>
                    <td>{formatDate(inv.invoice_date)}</td>
                    <td>{inv.firm_name}</td>
                    <td>
                      <div>{inv.buyer_name}</div>
                      {inv.buyer_client_id && (
                        <div className="text-xs t-muted t-mono">{inv.buyer_client_id}</div>
                      )}
                      {inv.creator?.name && (
                        <div className="text-xs t-muted">by {inv.creator.name}</div>
                      )}
                    </td>
                    <td>
                      {inv.gst_treatment === "gst" ? (
                        <span className="badge badge-info">
                          {inv.inter_state ? "IGST" : "CGST+SGST"}
                        </span>
                      ) : (
                        <span className="badge badge-neutral">No GST</span>
                      )}
                    </td>
                    <td className="t-mono">{formatINRExact(inv.grand_total)}</td>
                    <td>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <Link
                          to={`${ns}/invoices/${inv.id}`}
                          className="btn btn-secondary btn-sm"
                          title="Open print preview"
                        >
                          <Icon name="eye" size={13} /> View
                        </Link>
                        {canEdit && (
                          <Link
                            to={`${ns}/invoices/${inv.id}/edit`}
                            className="icon-btn"
                            title="Edit this invoice"
                          >
                            <Icon name="edit" size={13} />
                          </Link>
                        )}
                        {canEdit && (
                          <button
                            className="icon-btn"
                            style={{ color: "var(--danger)" }}
                            onClick={() => remove(inv)}
                            disabled={deletingId === inv.id}
                            title="Delete this invoice"
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={invoices.loading}
          />
        </div>
      </div>
    </>
  );
}
